import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import camelcase from 'camelcase'
import decamelize from 'decamelize'
import { clearSession, refreshSession, TOKEN_KEY } from '@/datasources/session'
import { emitBillingBlocked } from '@/datasources/billingBus'
import type { BillingBlockedPayload } from '@/types/billing'

export type ApiClientResponse<D = unknown> = {
  code: number
  messages: string | string[]
  data: D
}

export type ApiPaginatedResponse<D = unknown> = ApiClientResponse<D[]> & {
  total: number
  page: number
  pageSize: number
}

const REFRESH_URL = '/api/v1/auth/refresh'

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

// Fields whose nested keys should NOT be case-converted
// (opaque JSONB data from external providers — preserve original structure)
// orgId: backend zValidator schemas require camelCase body keys (e.g. createApiKeySchema)
const SKIP_CASE_CONVERT = new Set([
  'spec',
  'result',
  'stateJson',
  'details',
  'condition',
  'notifications',
  'state_json',
  'stateJson',
  'orgId',
  // Backend zValidator schemas require camelCase body keys — converting them
  // to snake_case would fail validation (e.g. /auth/refresh → 4006 Required).
  'refreshToken',
  'displayName',
  'avatarUrl',
  'appPermissions',
  'versionNumber',
  'changeSummary',
  'autoRollback',
  'rollbackThreshold',
  'deploymentStrategy',
  'commitSha',
  'commitMessage',
  // Backend zValidator schemas require camelCase body keys (issue #33)
  'monthlyBudgetCapCents',
  'budgetAlertThresholdPercent',
  // Billing (issue #38): topupCreateSchema / createTicketSchema /
  // realnameUpsertSchema / adminAdjustmentSchema all validate camelCase keys —
  // decamelizing them made every money write fail with 4006 Required.
  'amountCents',
  'taxId',
  'contactEmail',
  'titleType',
  'userId',
  // Billing AMR weight map is keyed by resource-type enums (issue #40) —
  // camelizing mangles them (ALIYUN_FC3_FUNCTION → aLIYUNFC3FUNCTION).
  'amrWeights',
  // deployment events follow the CLI's snake_case contract (ADR-005); the
  // TimelinePanel reads logical_id/duration_ms/error_message/created_at
  // verbatim, so camelizing would break it.
  'events',
])

// Request interceptor — inject auth token + convert camelCase params to snake_case
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Convert request body keys from camelCase → snake_case
  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    config.data = decamelizeKeys(config.data)
  }

  // Convert query params keys from camelCase → snake_case
  if (config.params && typeof config.params === 'object') {
    config.params = decamelizeKeys(config.params)
  }

  // Add org_id to every request if an active org is set
  const orgId = localStorage.getItem('si-active-org')
  if (orgId) {
    config.params = { ...config.params, org_id: orgId }
  }

  // Cache-bust GET requests
  if (config.method === 'get' && !config.params?._t) {
    config.params = { ...config.params, _t: Date.now() }
  }

  return config
})

// Single-flight: concurrent 401s share one refresh call; reset on settle.
// The refresh itself lives in datasources/session (one implementation shared
// with any proactive caller) — this wrapper only adds the de-duplication.
let refreshPromise: Promise<boolean> | null = null

const performRefresh = (): Promise<boolean> => {
  if (!refreshPromise) {
    refreshPromise = refreshSession()
      .then((result) => result.ok)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

const performLogout = (): void => {
  clearSession()
  window.location.href = '/login'
}

// Shared 401 path (HTTP status OR code 401): refresh once, retry once; on
// failure — or a failed refresh request / second 401 — clear creds and logout.
const handleUnauthorized = async (config: RetryableConfig): Promise<AxiosResponse> => {
  if (config.url?.includes(REFRESH_URL) || config._retried) {
    performLogout()
    throw new Error('Token refresh failed')
  }
  config._retried = true
  const refreshed = await performRefresh()
  if (!refreshed) {
    performLogout()
    throw new Error('Token refresh failed')
  }
  // Re-run through apiClient so interceptors re-inject the new Bearer token
  return apiClient.request(config)
}

// Response interceptor — convert snake_case response to camelCase + auto-refresh on 401
apiClient.interceptors.response.use(
  async (response) => {
    const data = response.data as ApiClientResponse

    // 401 → refresh once and retry the original request
    if (data.code === 401) {
      try {
        return await handleUnauthorized(response.config as RetryableConfig)
      } catch {
        return response
      }
    }

    // Convert response body keys from snake_case → camelCase
    if (response.data && typeof response.data === 'object') {
      response.data = camelizeKeys(response.data) as ApiClientResponse
    }

    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      return handleUnauthorized(error.config as RetryableConfig).catch(() => Promise.reject(error))
    }
    // Global billing-block interception (issue #33): 402/403 with billing codes
    // raises a UI event so any page can surface the blocked modal.
    const resp = error.response as AxiosResponse | undefined
    const code = resp?.data?.code
    if ((resp?.status === 402 || resp?.status === 403) && (code === 4020 || code === 4021)) {
      const payload = resp?.data?.data as BillingBlockedPayload | undefined
      if (payload) {
        const messages = resp?.data?.messages
        // Error bodies bypass the success-path camelize — normalize here so the
        // blocked modal reads camelCase (issue #38 balance context included).
        emitBillingBlocked(camelizeKeys(payload) as BillingBlockedPayload, Array.isArray(messages) ? messages : [String(messages ?? '')])
      }
    }
    return Promise.reject(error)
  },
)

/**
 * Recursively converts all object keys from camelCase to snake_case.
 * Skips fields that contain opaque JSONB data (SKIP_CASE_CONVERT).
 */
export function decamelizeKeys(value: unknown): unknown {
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value
  if (value instanceof Date) return value.toISOString()
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value
  if (Array.isArray(value)) return value.map(decamelizeKeys)

  const obj = value as Record<string, unknown>
  const result: Record<string, unknown> = {}

  for (const key of Object.keys(obj)) {
    const snakeKey = SKIP_CASE_CONVERT.has(key) ? key : decamelize(key, { separator: '_' })
    result[snakeKey] = SKIP_CASE_CONVERT.has(key) ? obj[key] : decamelizeKeys(obj[key])
  }

  return result
}

/**
 * Recursively converts all object keys from snake_case to camelCase.
 * Skips fields that contain opaque JSONB data (SKIP_CASE_CONVERT) —
 * their nested keys are preserved as-is so dotted resource ids (e.g.
 * "functions.probe") survive conversion.
 */
export function camelizeKeys(value: unknown): unknown {
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value
  if (value instanceof Date) return value.toISOString()
  if (typeof Blob !== 'undefined' && value instanceof Blob) return value
  if (Array.isArray(value)) return value.map(camelizeKeys)

  const obj = value as Record<string, unknown>
  const result: Record<string, unknown> = {}

  for (const key of Object.keys(obj)) {
    const skip = SKIP_CASE_CONVERT.has(key) || SKIP_CASE_CONVERT.has(camelcase(key))
    const camelKey = camelcase(key)
    result[camelKey] = skip ? obj[key] : camelizeKeys(obj[key])
  }

  return result
}

export { apiClient }
